import { useParams } from "react-router-dom";
import { ApiError } from "@/core/api/apiTypes";
import type { Business } from "@/features/business/types/businessTypes";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { FormBusinessBoundary } from "../components/FormBusinessBoundary";
import { FormEditor } from "../components/FormEditor";
import { FormPageState } from "../components/FormPageState";
import { FormUnavailable } from "../components/FormUnavailable";
import { useForm, useUpdateForm } from "../hooks/formQueries";

function EditForm({ business, formId }: { business: Business; formId: string }) {
  const query = useForm(business.id, formId);
  const mutation = useUpdateForm(business.id, formId);
  if (query.isLoading) return <FormPageState title="Edit form"><LoadingSpinner message="Loading form..." /></FormPageState>;
  const unavailable = query.error instanceof ApiError && (query.error.Status === 403 || query.error.Status === 404);
  if (unavailable || !query.data) return <FormPageState title="Edit form"><FormUnavailable error={query.error} onRetry={() => void query.refetch()} /></FormPageState>;
  return <FormEditor businessId={business.id} businessName={business.name} initialForm={query.data}
    onSave={(request, expectedVersion) => {
      if (expectedVersion === undefined) throw new Error("Reload this form before saving.");
      return mutation.mutateAsync({ ...request, expectedVersion });
    }}
    onReload={async () => {
      const result = await query.refetch();
      if (result.error) throw result.error;
      if (!result.data) throw new Error("This form is no longer available.");
      return result.data;
    }} />;
}

export function FormEditPage() {
  const { formId = "" } = useParams<{ formId: string }>();
  return <FormBusinessBoundary title="Edit form">{business => <EditForm key={`${business.id}:${formId}`} business={business} formId={formId} />}</FormBusinessBoundary>;
}
